import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Play } from "lucide-react";
import AaryaMark from "./AaryaMark";

/**
 * HeroCarousel - auto-rotating, swipeable, cinematic.
 * Returning users can hide the stories (preference persisted to localStorage).
 */
const STORIES = [
  {
    id: "aarti",
    img: "/hero/01_grandmother.png",
    who: "Aarti, 64 · Lucknow",
    quote: "My son lives in Sydney. With Aarya, he knows I'm okay, without me typing a word.",
  },
  {
    id: "eleanor",
    img: "/hero/03_eleanor.png",
    who: "Eleanor, 71 · Manchester",
    quote: "I check on my husband from the garden. He's just inside. But knowing matters.",
  },
  {
    id: "hiroshi",
    img: "/hero/04_hiroshi.png",
    who: "Hiroshi, 68 · Kyoto",
    quote: "My daughter is in California. We are 16 hours apart. Aarya keeps us in the same minute.",
  },
  {
    id: "amina",
    img: "/hero/06_amina.png",
    who: "Amina, 67 · Nairobi",
    quote: "My daughter is a nurse in London. I wait for her ping after every shift.",
  },
  {
    id: "mariana",
    img: "/hero/05_mariana.png",
    who: "Mariana, 32 · São Paulo",
    quote: "I work night shifts. Mum stops worrying the moment my 'I'm safe' lands.",
  },
];

const SLIDE_MS = 5800;
const HIDE_KEY = "aarya_hide_stories";

export default function HeroCarousel() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === "1"; } catch { return false; }
  });
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (paused || hidden) return;
    const t = setTimeout(() => setI((v) => (v + 1) % STORIES.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [i, paused, hidden]);

  const persist = (val) => {
    try { localStorage.setItem(HIDE_KEY, val ? "1" : "0"); } catch {}
  };
  const hideStories = () => { setHidden(true); persist(true); };
  const showStories = () => { setHidden(false); persist(false); setI(0); };

  const goTo = (idx) => setI(((idx % STORIES.length) + STORIES.length) % STORIES.length);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; setPaused(true); };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) goTo(dx < 0 ? i + 1 : i - 1);
    touchStartX.current = null;
    setTimeout(() => setPaused(false), 800);
  };

  // Hidden mode - compact branded header for returning users.
  if (hidden) {
    return (
      <div className="relative w-full bg-sand-100 border-b border-sand-200" data-testid="hero-collapsed">
        <div className="px-6 pt-6 pb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AaryaMark size={44}/>
            <div>
              <div className="font-display text-[24px] leading-none text-ink-900 tracking-tight">Aarya</div>
              <div className="text-[10px] tracking-[0.18em] text-terracotta-600 italic mt-1 font-semibold">From the Heart, For Every Home</div>
            </div>
          </div>
          <button
            onClick={showStories}
            data-testid="hero-show-stories"
            className="flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-sand-200 text-[12px] font-semibold text-ink-700 active:bg-sand-100"
          >
            <Play size={12}/> Show stories
          </button>
        </div>
      </div>
    );
  }

  const s = STORIES[i];

  return (
    <div
      className="relative h-[44dvh] min-h-[300px] w-full overflow-hidden bg-ink-900"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="hero-carousel"
    >
      <AnimatePresence initial={false} mode="sync">
        <motion.img
          key={s.id}
          src={s.img}
          alt={s.who}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1.0 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ opacity: { duration: 1.1 }, scale: { duration: 6.4, ease: "easeOut" } }}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </AnimatePresence>

      <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/45 to-transparent pointer-events-none"/>
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent via-sand-50/40 to-sand-50 pointer-events-none"/>

      <div className="absolute top-3 left-6 right-6 flex items-start justify-between z-10">
        <div className="flex items-center gap-2.5">
          <AaryaMark size={36}/>
          <div>
            <div className="font-display text-[20px] leading-none text-white tracking-tight" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.55)" }}>Aarya</div>
            <div className="text-[10px] tracking-[0.18em] text-white/90 italic mt-1" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>From the Heart, For Every Home</div>
          </div>
        </div>
        <button
          onClick={hideStories}
          data-testid="hero-hide-stories"
          className="flex items-center gap-1 px-2.5 h-8 rounded-full bg-black/35 backdrop-blur text-[11px] text-white/95 font-medium active:bg-black/55"
          aria-label="Hide stories"
        >
          <X size={12} strokeWidth={2.4}/> Skip
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={s.id + "-text"}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.55, delay: 0.25 }}
          className="absolute bottom-14 left-6 right-6 z-10"
          data-testid={`hero-story-${s.id}`}
        >
          <p className="text-[11px] tracking-[0.18em] uppercase text-white/95 font-semibold" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}>{s.who}</p>
          <p className="font-display text-[18px] leading-snug text-white mt-1 italic" style={{ textShadow: "0 1px 14px rgba(0,0,0,0.75)" }}>
            &ldquo;{s.quote}&rdquo;
          </p>
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-1.5 z-10">
        {STORIES.map((st, idx) => (
          <button
            key={st.id}
            onClick={() => goTo(idx)}
            aria-label={`Show story ${idx + 1}`}
            data-testid={`hero-dot-${idx}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/45"}`}
          />
        ))}
      </div>
    </div>
  );
}
